import { useEffect, useState } from "react";
import { Bell, BellOff, BellRing } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const b64ToU8 = (s) => {
  const pad = "=".repeat((4 - (s.length % 4)) % 4);
  const raw = atob((s + pad).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

const supported = () => "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

export default function PushToggle({ compact = false }) {
  const [state, setState] = useState("loading"); // loading | unsupported | off | on | denied
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!supported()) { setState("unsupported"); return; }
    if (Notification.permission === "denied") { setState("denied"); return; }
    navigator.serviceWorker.ready.then((reg) => reg.pushManager.getSubscription())
      .then((sub) => setState(sub ? "on" : "off"))
      .catch(() => setState("off"));
  }, []);

  const enable = async () => {
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { setState(perm === "denied" ? "denied" : "off"); toast.error("Permesso notifiche non concesso"); return; }
      const reg = await navigator.serviceWorker.ready;
      const { data } = await api.get("/push/vapid-public-key");
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToU8(data.public_key) });
      const j = sub.toJSON();
      await api.post("/push/subscribe", { endpoint: j.endpoint, keys: j.keys });
      await api.post("/push/test");
      setState("on");
      toast.success("Notifiche attivate");
    } catch {
      toast.error("Impossibile attivare le notifiche");
    } finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const j = sub.toJSON();
        await api.post("/push/unsubscribe", { endpoint: j.endpoint, keys: j.keys }).catch(() => {});
        await sub.unsubscribe();
      }
      setState("off");
      toast.success("Notifiche disattivate");
    } finally { setBusy(false); }
  };

  if (state === "loading" || state === "unsupported") return null;

  const on = state === "on";
  const Icon = on ? BellRing : state === "denied" ? BellOff : Bell;
  const label = on ? "Notifiche attive" : state === "denied" ? "Notifiche bloccate" : "Attiva notifiche";

  return (
    <button
      data-testid="push-toggle-btn"
      disabled={busy || state === "denied"}
      onClick={on ? disable : enable}
      title={state === "denied" ? "Sblocca le notifiche dalle impostazioni del browser" : label}
      className={`w-full flex items-center gap-2 ${compact ? "justify-center" : ""} px-3 py-2 rounded-lg border text-sm transition disabled:opacity-60 ${on ? "border-[#16A34A]/40 bg-[#F0FDF4] text-[#166534]" : "border-[#E8E2D8] text-[#6B6558] hover:bg-white"}`}
    >
      <Icon className={`w-4 h-4 ${on ? "text-[#16A34A]" : ""}`} />
      <span>{busy ? "Attendi…" : label}</span>
    </button>
  );
}
